import React, { useState, useEffect} from 'react';
import { Card, Button, ProgressBar, Badge } from 'react-bootstrap';
import { 
  FaArrowTrendUp, 
  FaArrowTrendDown,
  FaReceipt,
  FaTicket,
  FaPercent,
  FaCircleXmark,
  FaEye,
  FaChevronRight,
  FaClock,
  FaFileLines,
  FaUtensils,
  FaChartLine,
  FaTableList
} from 'react-icons/fa6';
import moment from 'moment';
import { useNavigate} from 'react-router-dom';
import { formatCurrency, getPermissionState } from '../../../../../../utils/functions';
import { getTotalChargesByLocalId } from '../../../../../../crud/management_api/mesas_info';
import ModalPropinasDetail from '../Stats/StatPropinas/components/ModalPropinasDetail/ModalPropinasDetail';
import SessionBriefModal from '../../../../../Restaurant/Caja/Ventas/components/GeneralSales/components/SessionBriefModal/SessionBriefModal';
import './FinancialSummary.scss';

export interface ReferenceValues {
  sales: {
    daily_variation: number | null;
    weekly_variation: number | null;
    today_sales_restaurant: number | null;
    yesterday_variation: number | null;
    last_week_variation: number | null;
    yesterday_sales: number | null;
    last_week_sales: number | null;
    daily_average: number | null;
    weekly_average: number | null;
  };
  tips: {
    daily_variation: number | null;
    weekly_variation: number | null;
    yesterday_variation: number | null;
    last_week_variation: number | null;
    yesterday_tip: number | null;
    last_week_tip: number | null;
    daily_average: number | null;
    weekly_average: number | null;
  };
}

export interface UserStats {
  name: string;
  total: number;
  count: number;
  avg_total: number;
  tip: number;
  avg_tip: number;
  cancelled: number;
  cancelled_amount: number;
  avg_cancelled: number;
  avg_tip_variation: number;
}

export interface TypeStats {
  name: string;
  total: number;
  count: number;
  avg_total: number;
}

export interface ShiftStatistics {
  shift_id: number;
  name: string;
  start_time: string;
  end_time: string | null;
  active: boolean;
  open_user_id: number;
  close_user_id: number | null;
  open_user_name: string;
  close_user_name: string | null;
  total_charged: number;
  gross_sales: number;
  valid_receipt_count: number;
  users_count: number;
  net_sales: number;
  pending_payments_count: number;
  total_pending: number;
  gross_loss: number;
  net_loss: number;
  cancelled_receipt_count: number;
  avg_ticket: number;
  total_tip: number;
  avg_tip_by_ticket: number;
  total_discounts: number;
  total_coupons: number;
  avg_tip_by_user: number;
  avg_ticket_pp?: number;
  total_people_served?: number;
  users_stats: { [userId: string]: UserStats };
  type_stats: { [typeId: string]: TypeStats };
}

export interface SalesStatistics {
  total_charged: number;
  gross_sales: number;
  valid_receipt_count: number;
  users_count: number;
  net_sales: number;
  pending_payments_count: number;
  total_pending: number;
  gross_loss: number;
  net_loss: number;
  cancelled_receipt_count: number;
  avg_ticket: number;
  users_stats: { [userId: string]: UserStats };
  type_stats: { [typeId: string]: TypeStats };
  total_tip: number;
  avg_tip_by_ticket: number;
  total_discounts: number;
  total_coupons: number;
  avg_tip_by_user: number;
  avg_ticket_pp?: number;
  total_people_served?: number;
  shifts?: { [shiftId: string]: ShiftStatistics };
  referenceValues: ReferenceValues;
}

interface FinancialSummaryProps {
  data: SalesStatistics | null;
}

const FinancialSummary: React.FC<FinancialSummaryProps> = ({ data }) => {
  const navigate = useNavigate();
  const [showTipDetails, setShowTipDetails] = useState(false);
  const [showSessionBrief, setShowSessionBrief] = useState(false);
  const [salesVariation, setSalesVariation] = useState<string | null>(null);
  const [tipVariation, setTipVariation] = useState<string | null>(null);
  const [tableCharges, setTableCharges] = useState<{ total_mesas: number; total_charges: number } | null>(null);

  useEffect(() => {
    if (data?.referenceValues) {
      // Sales variation
      if (data.referenceValues.sales.daily_variation !== null) {
        const raw = data.referenceValues.sales.daily_variation * 100;
        setSalesVariation(`${raw > 0 ? '+' : ''}${raw.toFixed(1)}%`);
      }
      
      // Tip variation
      if (data.referenceValues.tips.daily_variation !== null) {
        const raw = data.referenceValues.tips.daily_variation * 100;
        setTipVariation(`${raw > 0 ? '+' : ''}${raw.toFixed(1)}%`);
      }
    }
  }, [data]);

  // Fetch pending table charges (mesas used but not charged)
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') ?? '{}');
    const localId = user ? Number(user.local_id) : 0;
    
    if (localId > 0) {
      getTotalChargesByLocalId(localId)
        .then((response) => {
          setTableCharges(response.data);
        })
        .catch((error) => {
          console.error('Error fetching table charges:', error);
          setTableCharges(null);
        });
    }
  }, [data]); // Re-fetch when data changes (session refresh)

  const getVariationIcon = (variation: string | null) => {
    if (!variation) return null;
    return variation.startsWith('-') ? 
      <FaArrowTrendDown className="trend-icon negative" /> : 
      <FaArrowTrendUp className="trend-icon positive" />;
  };

  const getVariationClass = (variation: string | null) => {
    if (!variation) return '';
    return variation.startsWith('-') ? 'negative' : 'positive';
  };

  // Calculate average ticket
  const avgTicket = React.useMemo(() => {
    if (!data) return 0;
    const allTypeStats = Object.values(data.type_stats);
    const totalCount = allTypeStats.reduce((sum, t) => sum + t.count, 0);
    const totalSum = allTypeStats.reduce((sum, t) => sum + t.total, 0);
    return totalCount > 0 ? totalSum / totalCount : 0;
  }, [data]);

  // Calculate completion percentage for visual progress
  const salesProgress = data?.referenceValues.sales.daily_average 
    ? Math.min((data.net_sales / data.referenceValues.sales.daily_average) * 100, 100)
    : 0;

  const tipProgress = data?.referenceValues.tips.daily_average 
    ? Math.min((data.total_tip / data.referenceValues.tips.daily_average) * 100, 100)
    : 0;

  if (!data) return null;

  return (
    <div className="financial-summary">
      {/* Main Metrics Grid */}
      <div className="fs-metrics-grid">
        {/* Primary Sales Card with Shifts */}
        <Card className="fs-card fs-card-primary">
          <Card.Body>
            <div className="fs-metric-header">
              <div className="fs-metric-label">Ventas Netas</div>
              <div className={`fs-variation ${getVariationClass(salesVariation)}`}>
                {getVariationIcon(salesVariation)}
                {salesVariation || '0%'}
              </div>
            </div>
            <div className="fs-metric-value primary">
              {formatCurrency(data.net_sales)}
            </div>
            <div className="fs-metric-progress">
              <ProgressBar 
                now={salesProgress} 
                className="fs-progress-bar"
                variant="warning"
              />
              <span className="fs-progress-label">
                {salesProgress.toFixed(0)}% del objetivo diario
              </span>
            </div>

            {/* Pending Payments Indicator */}
            {data.total_pending > 0 && (
              <div className="fs-pending-indicator">
                <div className="fs-pending-icon-wrapper">
                  <FaClock className="fs-pending-icon" />
                </div>
                <div className="fs-pending-content">
                  <span className="fs-pending-label">Pendiente de pago</span>
                  <span className="fs-pending-value">{formatCurrency(data.total_pending)}</span>
                </div>
                {data.pending_payments_count > 0 && (
                  <Badge bg="warning" className="fs-pending-badge">
                    {data.pending_payments_count} {data.pending_payments_count === 1 ? 'cuenta' : 'cuentas'}
                  </Badge>
                )}
              </div>
            )}

            {/* Pending Table Charges Indicator (mesas used but not charged) */}
            {tableCharges && tableCharges.total_charges > 0 && (
              <div className="fs-pending-indicator fs-pending-tables">
                <div className="fs-pending-icon-wrapper fs-tables-icon-wrapper">
                  <FaUtensils className="fs-pending-icon" />
                </div>
                <div className="fs-pending-content">
                  <span className="fs-pending-label">Pendiente en mesa</span>
                  <span className="fs-pending-value">{formatCurrency(tableCharges.total_charges)}</span>
                </div>
                {tableCharges.total_mesas > 0 && (
                  <Badge bg="info" className="fs-pending-badge fs-tables-badge">
                    {tableCharges.total_mesas} {tableCharges.total_mesas === 1 ? 'mesa' : 'mesas'}
                  </Badge>
                )}
              </div>
            )}

            {/* Approximate Daily Sales - Sum of net sales + pending payments + pending in tables */}
            {(data.total_pending > 0 || (tableCharges && tableCharges.total_charges > 0)) && (
              <div className="fs-approximate-sales">
                <div className="fs-metric-header">
                  <div className="fs-metric-label fs-approximate-label">Venta Aproximada del Día</div>
                  <FaChartLine className="fs-approximate-icon" />
                </div>
                <div className="fs-metric-value fs-approximate-value">
                  {formatCurrency(data.net_sales + data.total_pending + (tableCharges?.total_charges || 0))}
                </div>
              </div>
            )}

            {/* Session Report Button */}
            <Button 
              variant="link" 
              size="sm" 
              className="fs-detail-btn"
              onClick={() => setShowSessionBrief(true)}
            >
              <FaFileLines /> Ver resumen de sesión <FaChevronRight />
            </Button>

            {/* Compact Shifts Section Inside Sales Card */}
            {data.shifts && Object.keys(data.shifts).length > 0 && (
              <div className="fs-shifts-inline">
                <div className="fs-shifts-inline-header">
                  <FaClock className="fs-shifts-inline-icon" />
                  <span>Desglose por Turno</span>
                </div>
                {Object.values(data.shifts).map((shift) => {
                  const shiftPercentage = data.net_sales > 0 ? (shift.net_sales / data.net_sales) * 100 : 0;
                  const duration = shift.end_time 
                    ? moment(shift.end_time).diff(moment(shift.start_time), 'hours', true)
                    : moment().diff(moment(shift.start_time), 'hours', true);
                  
                  return (
                    <div key={shift.shift_id} className="fs-shift-inline-row">
                      <div className="fs-shift-inline-info">
                        <div className="fs-shift-inline-title">
                          <span className="fs-shift-inline-name">{shift.name}</span>
                          <Badge bg={shift.active ? 'success' : 'secondary'} className="fs-shift-inline-badge">
                            {shift.active ? 'Activo' : 'Cerrado'}
                          </Badge>
                        </div>
                        <span className="fs-shift-inline-time">
                          {moment(shift.start_time).format('HH:mm')}
                          {shift.end_time && ` - ${moment(shift.end_time).format('HH:mm')}`}
                          <span className="fs-shift-inline-duration">• {duration.toFixed(1)}h</span>
                        </span>
                      </div>
                      <div className="fs-shift-inline-metrics">
                        <div className="fs-shift-inline-metric">
                          <span className="fs-shift-inline-metric-value">{formatCurrency(shift.net_sales)}</span>
                          <span className="fs-shift-inline-metric-label">{shiftPercentage.toFixed(1)}%</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </Card.Body>
        </Card>

        {/* Tips Card */}
        <Card className="fs-card fs-card-secondary">
          <Card.Body>
            <div className="fs-metric-header">
              <div className="fs-metric-label">Propinas</div>
              <div className={`fs-variation ${getVariationClass(tipVariation)}`}>
                {getVariationIcon(tipVariation)}
                {tipVariation || '0%'}
              </div>
            </div>
            <div className="fs-metric-value secondary">
              {formatCurrency(data.total_tip)}
            </div>
            <div className="fs-metric-progress">
              <ProgressBar 
                now={tipProgress} 
                className="fs-progress-bar fs-progress-bar-tips"
              />
              <span className="fs-progress-label">
                Promedio: {formatCurrency(data.avg_tip_by_user)}
              </span>
            </div>

            {/* Compact Shifts Tips Section Inside Tips Card */}
            {data.shifts && Object.keys(data.shifts).length > 0 && (
              <div className="fs-shifts-inline">
                <div className="fs-shifts-inline-header">
                  <FaClock className="fs-shifts-inline-icon" />
                  <span>Desglose por Turno</span>
                </div>
                {Object.values(data.shifts).map((shift) => {
                  const shiftTipPercentage = data.total_tip > 0 ? (shift.total_tip / data.total_tip) * 100 : 0;
                  
                  return (
                    <div key={shift.shift_id} className="fs-shift-inline-row">
                      <div className="fs-shift-inline-info">
                        <div className="fs-shift-inline-title">
                          <span className="fs-shift-inline-name">{shift.name}</span>
                          <Badge bg={shift.active ? 'success' : 'secondary'} className="fs-shift-inline-badge">
                            {shift.active ? 'Activo' : 'Cerrado'}
                          </Badge>
                        </div>
                      </div>
                      <div className="fs-shift-inline-metrics">
                        <div className="fs-shift-inline-metric">
                          <span className="fs-shift-inline-metric-value">{formatCurrency(shift.total_tip)}</span>
                          <span className="fs-shift-inline-metric-label">{shiftTipPercentage.toFixed(1)}%</span>
                        </div>
                        <div className="fs-shift-inline-metric">
                          <span className="fs-shift-inline-metric-value">{formatCurrency(shift.avg_tip_by_user)}</span>
                          <span className="fs-shift-inline-metric-label">Promedio</span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            <div className="fs-tip-actions">
              <Button 
                variant="link" 
                size="sm" 
                className="fs-detail-btn"
                onClick={() => setShowTipDetails(true)}
              >
                <FaEye /> Ver detalles <FaChevronRight />
              </Button>

              {getPermissionState(135) && (
                <Button 
                  variant="link" 
                  size="sm" 
                  className="fs-detail-btn fs-planilla-btn"
                  onClick={() => navigate("/App/TipManagement")}
                >
                  <FaTableList /> Cálculo Planilla <FaChevronRight />
                </Button>
              )}
            </div>
          </Card.Body>
        </Card>

        {/* Compact Quick Stats Card */}
        <Card className="fs-card fs-quick-stats-card">
          <Card.Body>
            <div className="fs-quick-stats-grid">
              <div className="fs-quick-stat-item">
                <FaReceipt className="fs-quick-stat-icon" />
                <div className="fs-quick-stat-content">
                  <span className="fs-quick-stat-value">{formatCurrency(avgTicket)}</span>
                  <span className="fs-quick-stat-label">Ticket Prom.</span>
                </div>
              </div>
              
              {data.avg_ticket_pp != null && data.avg_ticket_pp > 0 && (
                <div className="fs-quick-stat-item">
                  <FaUtensils className="fs-quick-stat-icon" />
                  <div className="fs-quick-stat-content">
                    <span className="fs-quick-stat-value">{formatCurrency(data.avg_ticket_pp)}</span>
                    <span className="fs-quick-stat-label">Ticket Prom. pp</span>
                  </div>
                </div>
              )}
              
              <div className="fs-quick-stat-item">
                <FaCircleXmark className="fs-quick-stat-icon danger" />
                <div className="fs-quick-stat-content">
                  <span className="fs-quick-stat-value">{data.cancelled_receipt_count}</span>
                  <span className="fs-quick-stat-label">Cancelados</span>
                </div>
              </div>
              
              <div className="fs-quick-stat-item">
                <FaTicket className="fs-quick-stat-icon warning" />
                <div className="fs-quick-stat-content">
                  <span className="fs-quick-stat-value">{formatCurrency(data.total_coupons)}</span>
                  <span className="fs-quick-stat-label">Cupones</span>
                </div>
              </div>
              
              <div className="fs-quick-stat-item">
                <FaPercent className="fs-quick-stat-icon info" />
                <div className="fs-quick-stat-content">
                  <span className="fs-quick-stat-value">{formatCurrency(data.total_discounts)}</span>
                  <span className="fs-quick-stat-label">Descuentos</span>
                </div>
              </div>
            </div>
          </Card.Body>
        </Card>

        {/* Sales Breakdown */}
        <Card className="fs-card fs-breakdown">
          <Card.Body>
            <div className="fs-breakdown-header">
              <span className="fs-breakdown-title">Desglose por Tipo</span>
            </div>
            <div className="fs-breakdown-list">
              {Object.values(data.type_stats).map((typeStat, idx) => {
                const percentage = data.net_sales > 0 ? (typeStat.total / data.net_sales) * 100 : 0;
                return (
                  <div key={idx} className="fs-breakdown-item">
                    <div className="fs-breakdown-info">
                      <span className="fs-breakdown-name">{typeStat.name}</span>
                      <span className="fs-breakdown-percentage">{percentage.toFixed(1)}%</span>
                    </div>
                    <div className="fs-breakdown-value">
                      {formatCurrency(typeStat.total)}
                    </div>
                    <ProgressBar 
                      now={percentage} 
                      className="fs-breakdown-bar"
                      variant="primary"
                    />
                  </div>
                );
              })}
            </div>
          </Card.Body>
        </Card>
      </div>

      {/* Modal for tip details */}
      <ModalPropinasDetail
        show={showTipDetails}
        onHide={() => setShowTipDetails(false)}
        data={data}
      />

      {/* Modal for session brief/report */}
      <SessionBriefModal
        show={showSessionBrief}
        onHide={() => setShowSessionBrief(false)}
      />
    </div>
  );
};

export default FinancialSummary;
